import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Global Website Theme Context.
 *
 * Source of truth: `global_settings.theme_mode` on the backend, exposed via
 *   GET  /api/cms/theme  (public, read-only)
 *   PUT  /api/cms/theme  (admin-only)
 *
 * - Public visitors: read-only. They receive whatever the admin has set.
 * - Admin: toggles via Admin → Profile → Appearance. That PUTs /cms/theme,
 *   and the new theme is applied immediately in this tab. Other tabs pick it
 *   up on next mount/reload.
 *
 * A cached copy is written to localStorage under key `wappit_theme` purely to
 * prevent FOUC — the inline script in public/index.html reads it synchronously
 * and applies .dark on <html> before React renders. If the server says
 * otherwise on mount, we reconcile.
 *
 * Modes supported: "light" | "dark". (No system mode — a single admin toggle
 * controls both the public website and the admin dashboard uniformly.)
 */

const STORAGE_KEY = "wappit_theme";
const ALLOWED = new Set(["light", "dark"]);

const ThemeCtx = createContext({
  theme: "light",
  setTheme: async () => {},
});

const applyThemeToDom = (mode) => {
  const root = document.documentElement;
  const prev = root.classList.contains("dark") ? "dark" : "light";
  if (prev === mode) {
    root.setAttribute("data-theme", mode);
    root.style.colorScheme = mode;
    return;
  }
  root.classList.add("theme-transitioning");
  if (mode === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.setAttribute("data-theme", mode);
  root.style.colorScheme = mode;
  window.setTimeout(() => root.classList.remove("theme-transitioning"), 260);
};

const readCached = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return ALLOWED.has(v) ? v : "light";
  } catch (_) { return "light"; }
};

const writeCached = (mode) => {
  try { localStorage.setItem(STORAGE_KEY, mode); } catch (_) { /* ignore */ }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readCached);

  // Apply DOM whenever local state changes
  useEffect(() => {
    applyThemeToDom(theme);
    writeCached(theme);
  }, [theme]);

  // On mount: sync from server (source of truth)
  useEffect(() => {
    let cancelled = false;
    api
      .get("/cms/theme")
      .then((r) => {
        if (cancelled) return;
        const remote = r?.data?.theme_mode;
        if (ALLOWED.has(remote) && remote !== theme) {
          setThemeState(remote);
        }
      })
      .catch(() => { /* silent — cached value remains */ });
    return () => { cancelled = true; };
  }, []);

  const setTheme = useCallback(async (next) => {
    if (!ALLOWED.has(next)) return;
    // Optimistic local apply
    setThemeState(next);
    // Persist to server (admin only). Silently ignore non-admin failures.
    try {
      const token = localStorage.getItem("wappit_admin_token");
      if (!token) return;
      await api.put("/cms/theme", { theme_mode: next });
    } catch (_) { /* server rejected — leave cache */ }
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
};

export const useTheme = () => useContext(ThemeCtx);
